import mixins from 'vue-typed-mixins';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { ErrorResponse, FileExportMixin } from '@codequality/vue-cli-base';

import { E帳票種別, Eフェーズ種別 } from '@/openapi/enum';
import {
  データApi,
  データListItemResponse,
  勘定科目Api,
  帳票Api,
  帳票ListItemRequest,
  計画書データApi,
  計画書データListItemResponse,
  計画書データRequest,
  部門Api,
} from '@/openapi/user';

import PlbscfReadonlyValue from '@/components/plbscfReadonlyValue/PlbscfReadonlyValue.vue';
import C決算期TableHeader from '@/components/決算期TableHeader/決算期TableHeader.vue';

import AlertMixin from '@/mixins/AlertMixin';
import ListMixin from '@/entries/user/mixins/ListMixin';
import RoutesMixin from '@/entries/user/mixins/RoutesMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import AppFormatUtility from '@/classes/common/AppFormatUtility';
import StorageDao from '@/classes/common/StorageDao';
import UserApiUtility from '@/classes/common/UserApiUtility';
import { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractデータService from '@/classes/service/AbstractデータService';
import 実態純資産Service from '@/classes/service/計画/実態純資産Service';

// 比較項目に紐づく計算Code（実態純資産は空文字を設定）と単位（金額以外の場合設定）
const ComparisonLabel: { [key: string]: { code: string; unitNotYen?: string } } = {
  売上高: { code: 'N201' },
  売上総利益: { code: 'N214' },
  売上総利益率: { code: 'N215', unitNotYen: '%' },
  営業利益: { code: 'N217' },
  経常利益: { code: 'N220' },
  FCF: { code: 'N704' },
  有利子負債残高: { code: 'N705' },
  現預金残高: { code: 'N706' },
  債務償還年数: { code: 'N708', unitNotYen: '' },
  実態純資産: { code: '' },
};
// 要約比較表示用
type Summary = {
  header: 決算期TableHeader[]; // size：年数
  list: SummaryListItem[]; // size：行数
};
type SummaryListItem = {
  label: string;
  summaryComparisons: SummaryComparison[]; // size：計画書データ数（2 or 3）
  unitNotYen?: string; // 金額以外の場合は要設定（単位なしの場合は空文字）
};
type SummaryComparison = {
  name: string;
  values: number[]; // size：年数
};
type 入力計画書 = Omit<
  計画書データListItemResponse,
  | 'c計画書データ番号'
  | 'c実績直近月'
  | 'c計画0期の決算期'
  | 'c過去データ表示フラグ'
  | 'c第1期月次作成フラグ'
  | 'c千円表示フラグ'
  | 'c分析0円非表示フラグ'
  | 'c計画0円非表示フラグ'
  | 'cモニタリング0円非表示フラグ'
  | 'c計画表示モード'
  | 'cフリーキャッシュフロー種別'
  | 'c更新日時'
> & {
  c計画書データ番号?: number;
  c実績直近月?: number;
};

const initialEdit計画書 = (): 入力計画書 => {
  return {
    pk: 0,
    c計画書データ名: '',
    c備考: '',
  };
};

export default mixins(AlertMixin, FileExportMixin, ListMixin, RoutesMixin, 事業所Mixin, 計画書Mixin).extend({
  name: 'P計画書',

  components: {
    PlbscfReadonlyValue,
    C決算期TableHeader,
  },

  data(): {
    api: {
      kd: 計画書データApi;
      d: データApi;
      k: 勘定科目Api;
      b: 部門Api;
      o: 帳票Api;
    };
    計画書s: 計画書データListItemResponse[];
    edit計画書: 入力計画書;
    selected計画書s: 計画書データListItemResponse[];
    service: 実態純資産Service;
    summary: Summary;
    summaryDialogVisible: boolean;
  } {
    return {
      api: {
        kd: UserApiUtility.ogApi(計画書データApi),
        d: UserApiUtility.ogApi(データApi),
        k: UserApiUtility.ogApi(勘定科目Api),
        b: UserApiUtility.ogApi(部門Api),
        o: UserApiUtility.ogApi(帳票Api),
      },
      計画書s: [],
      edit計画書: initialEdit計画書(),
      selected計画書s: [],
      service: new 実態純資産Service(Eフェーズ種別.計画フェーズ),
      summary: {
        header: [],
        list: [],
      },
      summaryDialogVisible: false,
    };
  },

  computed: {
    canUpdate(): boolean {
      return this.edit計画書.pk > 0;
    },
    summaryDialogTitle(): string {
      const names = this.selected計画書s.map((k) => k.c計画書データ名).join('と');
      return `${this.事業所.c事業所名}（${names}の比較）`;
    },
  },

  created(): void {
    this.$_load();
  },

  methods: {
    selectForEdit(v計画書: 計画書データListItemResponse): void {
      this.edit計画書 = _.cloneDeep(v計画書);
    },

    clearForm() {
      this.edit計画書 = initialEdit計画書();
    },

    async save(isNew: boolean): Promise<void> {
      // クライアント側で入力チェックをしたくないため、敢えてasを使用している
      const request = _.pick(this.edit計画書, [
        'c計画書データ番号',
        'c計画書データ名',
        'c実績直近月',
        'c備考',
      ]) as 計画書データRequest;
      const response: AxiosResponse<void | ErrorResponse> = isNew
        ? await this.api.kd.create計画書データ(this.事業所.pk, request)
        : await this.api.kd.update計画書データ(this.edit計画書.pk, request);

      if (this.isValidationErrorResponse(response)) return;
      this.$_clearAndLoad();
    },

    async copy(計画書pk: number): Promise<void> {
      if (!confirm('コピーしますか？')) return;

      const response = await this.api.kd.copy計画書データ(計画書pk);
      if (this.isValidationErrorResponse(response)) return;

      this.$_clearAndLoad();
    },

    async remove(計画書pk: number): Promise<void> {
      const isCancelOrError = await this.isCancelOrValidationErrorResponseToDelete(() =>
        this.api.kd.logicalDelete計画書データ(計画書pk)
      );
      if (isCancelOrError) return;
      this.$_clearAndLoad();
    },

    async goTo取込(計画書: 計画書データListItemResponse): Promise<void> {
      this.set計画書(計画書);
      await this.update事業所MenuLog();
      this.$router.push({
        name: this.routes.取込.name,
      });
    },

    async compare(): Promise<void> {
      if (![2, 3].includes(this.selected計画書s.length)) {
        window.alert('データは 2つ または 3つ 選択してください。');
        return;
      }
      // request作成
      const 勘定科目Request = this.api.k.list勘定科目(this.事業所.pk, undefined, undefined, undefined, {
        params: { sj: 'PLBSCF_計画' },
      });
      const 部門Request = this.api.b.list部門(this.事業所.pk);
      const e入力分類s = this.service.getRelationalE入力分類s();
      const データRequests = this.selected計画書s.map((k) =>
        this.api.d.listデータ(k.pk, e入力分類s, undefined, { params: { sj: 'PLBSCF' } })
      );
      // API呼び出し
      const [勘定科目Response, 部門Response, ...データResponses] = await Promise.all([
        勘定科目Request,
        部門Request,
        ...データRequests,
      ]);
      this.service.set勘定科目s = 勘定科目Response.data;
      this.service.set部門s = 部門Response.data;
      const datas = データResponses.map((r) => r.data);
      this.$_setSummaryComparison(datas);

      this.summaryDialogVisible = true;
    },

    async outputPdf(): Promise<void> {
      await this.$_output(E帳票種別.pdf);
    },

    async outputXlsx(): Promise<void> {
      await this.$_output(E帳票種別.xlsx);
    },

    async $_output(e帳票種別: E帳票種別): Promise<void> {
      // 要約比較の帳票出力：他で使わないためservice化していない
      // テンプレート行設定
      const templateConfig = {
        header: 0,
        row: {
          odd: 1,
          even: 3,
        },
        unitNotYenRow: {
          odd: 2,
          even: 4,
        },
      };
      // 以下帳票データ整形
      // 初行設定込みで初期化
      const templateRows: number[] = [templateConfig.header];
      const values: unknown[][] = [[null, null, ...this.summary.header.map((h) => h.label)]];

      this.summary.list.forEach((listItem) => {
        listItem.summaryComparisons.forEach((sc, j) => {
          const useRow = listItem.unitNotYen === undefined ? templateConfig.row : templateConfig.unitNotYenRow;
          templateRows.push(j % 2 === 0 ? useRow.odd : useRow.even);
          values.push([
            j === 0 ? listItem.label : '',
            sc.name,
            ...sc.values.map((v) =>
              listItem.unitNotYen === undefined ? AppFormatUtility.changeUnit(v, 1000) : v + listItem.unitNotYen
            ),
          ]);
        });
      });

      const request: 帳票ListItemRequest = {
        templateName: '要約比較',
        templateRows,
        values,
      };
      const printResponse = await this.api.o.output帳票(e帳票種別, [request], this.getExportOptions());
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.exportFile(printResponse, process.env.VUE_APP_IS_MOCK === 'true');
    },

    async $_load(): Promise<void> {
      const response = await this.api.kd.list計画書データ(this.事業所.pk);
      this.計画書s = response.data;
    },

    $_clearAndLoad() {
      this.clearForm();
      this.$_load();
    },

    $_setSummaryComparison(datas: データListItemResponse[][]): void {
      // 要約比較の画面表示作成：他で使わないためservice化していない
      let header: 決算期TableHeader[] = [];
      const list: SummaryListItem[] = Object.entries(ComparisonLabel).map(([label, value]) => ({
        label,
        summaryComparisons: [],
        unitNotYen: value.unitNotYen,
      }));

      // 計画書でループ
      for (let i = 0; i < this.selected計画書s.length; i++) {
        const v計画書 = this.selected計画書s[i];
        // service / 決算期Utility は、それぞれ AbstractデータService / storage の計画書が設定されている前提で動くため、ここで設定させている
        // TODO: 余裕があれば、決算期Utilityの各paramにdefault値を設定して対応する
        AbstractデータService.set計画書 = v計画書;
        StorageDao.save計画書(v計画書);
        StorageDao.save計画書Local({
          c第1期月次作成フラグ: v計画書.c第1期月次作成フラグ,
        });

        // 現在の計画書に紐づくtデータでparse
        const parsed = this.service.parse(datas[i]);
        if (header.length === 0) header = parsed.header.slice(1);
        const plbscfService = this.service.get計画PlbscfService!;

        // 計画PlbscfServiceから指定Codeの結果を年数分取得
        const getCodeValues = (code: string): number[] => {
          return header.map((h) => plbscfService.getValueByCodeAnd決算期年月(code, h.year));
        };

        // 項目ごとに値を取得
        list.forEach((listItem) => {
          const code = ComparisonLabel[listItem.label].code;
          listItem.summaryComparisons.push({
            name: v計画書.c計画書データ名,
            values: code.length === 0 ? parsed.合計.values.slice(1) : getCodeValues(code),
          });
        });
      }
      this.summary = {
        header,
        list,
      };
    },
  },
});
